import { Button, Card, Empty, Space, Table, Tag, App } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ExportConsents } from './ExportConsents.js';
import { downloadAdminReceiptsExport } from '../../diagnostics/export.js';
import { useGlobalStore } from '@digiwedge/global-state';
import { useCallback, useEffect, useState } from 'react';
import { TableToolbar } from '../ui/TableToolbar.js';
import { useUrlPagination } from '../hooks/useUrlPagination.js';
import { SkeletonPage } from '../ui/SkeletonPage.js';

type Artifact = {
  id: string;
  siteKey: string;
  format: string;
  mime: string;
  filename: string;
  size: number;
  createdAt: string;
};

export function ExportsPage({ registryUrl, siteKey }: { registryUrl: string; siteKey: string }) {
  const { message } = App.useApp();
  const [items, setItems] = useState<Artifact[]>([]);
  const [total, setTotal] = useState<number>(0);
  const { page, pageSize, q, setPage, setPageSize } = useUrlPagination(50);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const tok = useGlobalStore.getState().token;
      const ofs = (page - 1) * pageSize;
      const url = new URL(`${registryUrl}/admin/exports`);
      url.searchParams.set('siteKey', siteKey);
      url.searchParams.set('limit', String(pageSize));
      url.searchParams.set('offset', String(ofs));
      const r = await fetch(url.toString(), {
        headers: tok ? { Authorization: `Bearer ${tok}` } : {},
      });
      const j = (await r.json()) as { items: Artifact[]; total?: number };
      const filtered = q
        ? (j.items || []).filter(
            (a) =>
              a.filename.toLowerCase().includes(q.toLowerCase()) ||
              a.format.toLowerCase().includes(q.toLowerCase()),
          )
        : j.items || [];
      setItems(filtered);
      setTotal(j.total ?? filtered.length);
    } catch {
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [registryUrl, siteKey, page, pageSize, q]);
  useEffect(() => {
    void load();
  }, [load]);

  const columns: ColumnsType<Artifact> = [
    { title: 'File', dataIndex: 'filename', key: 'filename' },
    {
      title: 'Format',
      dataIndex: 'format',
      key: 'format',
      width: 120,
      render: (v) => <Tag>{v}</Tag>,
    },
    {
      title: 'Size',
      dataIndex: 'size',
      key: 'size',
      width: 120,
      render: (v: number) => `${Math.round(v / 1024)} KB`,
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 200,
      render: (v: string) => new Date(v).toLocaleString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_, r) => (
        <Space>
          <Button
            size="small"
            onClick={() =>
              window.open(`${registryUrl}/admin/exports/${encodeURIComponent(r.id)}`, '_blank')
            }
          >
            Download
          </Button>
          <Button
            size="small"
            onClick={async () => {
              await navigator.clipboard.writeText(
                `${registryUrl}/admin/exports/${encodeURIComponent(r.id)}`,
              );
              message.success('Link copied');
            }}
          >
            Copy link
          </Button>
        </Space>
      ),
    },
  ];
  const runArtifact = async (format: 'csv' | 'json' | 'jsonl' = 'csv') => {
    try {
      const tok = useGlobalStore.getState().token;
      const r = await fetch(`${registryUrl}/admin/exports/consents:start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
        },
        body: JSON.stringify({ siteKey, format, range: '30d' }),
      });
      const res = await r.json();
      if (!r.ok || !res.jobId) throw new Error(res.error || `HTTP ${r.status}`);
      const jobId = res.jobId as string;
      message.loading('Generating export…', 1);
      let tries = 0;
      const timer = setInterval(async () => {
        tries++;
        try {
          const jwt = useGlobalStore.getState().token;
          const jr = await fetch(`${registryUrl}/admin/exports/jobs/${encodeURIComponent(jobId)}`, {
            headers: jwt ? { Authorization: `Bearer ${jwt}` } : {},
          });
          const j = await jr.json();
          if (j.status === 'done' && j.artifactId) {
            const url = `${registryUrl}/admin/exports/${encodeURIComponent(j.artifactId)}`;
            await navigator.clipboard.writeText(url);
            message.success('Artifact ready — link copied');
            clearInterval(timer);
          } else if (j.status === 'error') {
            message.error(j.error || 'Export failed');
            clearInterval(timer);
          } else if (tries > 60) {
            message.warning('Export taking longer than expected…');
            clearInterval(timer);
          }
        } catch {
          clearInterval(timer);
        }
      }, 1000);
    } catch (e) {
      message.error((e as Error).message);
    }
  };
  const table = (
    <>
      <TableToolbar placeholder="Search exports" />
      {loading ? (
        <SkeletonPage />
      ) : items.length === 0 && !q ? (
        <Empty description="No exports yet" />
      ) : (
        <Table<Artifact>
          rowKey={(r) => r.id}
          dataSource={items}
          columns={columns}
          size="small"
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            onChange: (p, s) => {
              setPage(p);
              if (s !== pageSize) setPageSize(s);
            },
          }}
          loading={loading}
        />
      )}
    </>
  );
  return (
    <Card
      title="Exports"
      extra={
        <Space>
          <Button onClick={() => runArtifact('csv')}>Generate CSV artifact</Button>
          <Button onClick={() => runArtifact('jsonl')}>Generate JSONL artifact</Button>
          <Button onClick={load} loading={loading}>
            Refresh
          </Button>
        </Space>
      }
    >
      <ExportConsents registryUrl={registryUrl} siteKey={siteKey} />
      <Card size="small" style={{ marginTop: 12 }} title="Receipts">
        <Space>
          <Button
            onClick={() =>
              downloadAdminReceiptsExport({
                baseUrl: registryUrl,
                siteKey,
                fromIso: new Date(Date.now() - 7 * 86400000).toISOString(),
                toIso: new Date().toISOString(),
                format: 'jsonl',
                authToken: useGlobalStore.getState().token || undefined,
              }).catch((e) => message.error(String(e)))
            }
          >
            Download JSONL (7d)
          </Button>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              const cmd = 'node tools/receipts/validate.mjs receipts.jsonl keys.json';
              void navigator.clipboard
                .writeText(cmd)
                .then(() => message.success('Validator command copied'));
            }}
            title="Copy validator CLI command to clipboard"
          >
            Validate
          </a>
        </Space>
      </Card>
      <div style={{ marginTop: 12 }}>{table}</div>
    </Card>
  );
}
